import { useSubmit } from "@certn/api";
import { CascadiaColor, CascadiaIcon } from "@certn/cascadia-tokens";
import { formatFileSize, truncatedFileName } from "@certn/utils";
import { css } from "@emotion/css";
import { useEffect, useRef, useState } from "react";
import { FileDropItem } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { ActionList } from "../action-list/ActionList";
import { ActionListItem } from "../action-list/ActionListItem";
import { GhostIconButton } from "../button/GhostIconButton";
import { Link } from "../link/Link";
import { List } from "../list/List";
import { Loader } from "../loaders/loading/Loader";
import { Stack } from "../../foundations/grid-layout/Stack";
import { Text } from "../../foundations/typography/Text";

import { Upload, UploadProps } from "./Upload";
import { FileSubtitleRAC } from "./assets/FileSubtitle";
import { UploadError } from "./assets/UploadError";
import { DocumentCreateResponse } from "./assets/types";
import { FileInfo, fileToFileInfo } from "./assets/utils";
import { Icon } from "../../foundations/icons/Icon/Icon";
import {
  FieldErrorsProps,
  FieldErrors,
} from "../inputs/_assets/input-wrapper/FieldErrors";
import { FormFeedbackRAC } from "../inputs/_assets/input-wrapper/FormFeedbackRAC";
import { FormLabel } from "../inputs/_assets/input-wrapper/FormLabel";
import { FormText } from "../inputs/_assets/input-wrapper/FormText";

export interface FileUploadProps extends Omit<UploadProps, "onFileSelected"> {
  orientation?: "horizontal" | "vertical";
  label?: string;
  info?: React.ReactNode;
  isInvalid?: boolean;
  errors?: FieldErrorsProps["children"];
  setErrors?(errors: string[]): void;
  showFileInfo?: "size" | "type";
  files?: FileInfo[];
  onSelect?(file: File): Promise<void>;
  onRemove?(file: FileInfo): void;
  testID?: string;
  isRequired?: boolean;
  requestUrl: string;
  setFiles?(files: FileInfo[]): void;
  bucketDirectory?: string;
  onBlur?: () => void;
  description?: string;
  iconType?: CascadiaIcon;
  iconColor?: CascadiaColor;
  allowedFileTypes?: string[];
  downloadButtonInEditState?: boolean;
  tryOpenFile?: (fileKey: string | undefined) => void;
  caseId?: string;
  uploadType?: string;
  useTrueFileName?: boolean;
  /** A function to generate a custom file name for the uploaded file.
   * Defaults to a function that prepends a UUID to the original file name.
   *
   * @param file - The file object to generate a name for.
   * @returns The generated file name as a string.
   */
  generateFileName?: (file: File) => string;
  /** A flag indicating whether to overwrite existing files with the same name.
   * Defaults to false.
   */
  overwriteExistingFiles?: boolean;
  /** Text to display when the control is disabled and has no files.
   * Defaults to "No document found".
   */
  emptyStateText?: string;
  /** Returns the upload state.
   */
  onUpload?: (state: "init" | "success" | "failure") => void;
}

/**
 * Renders a file upload component with support for uploading multiple files.
 *
 * @param props - The component props.
 * @returns The rendered file upload component.
 */

export function FileUpload(props: FileUploadProps) {
  const {
    label,
    showFileInfo,
    files = [],
    setFiles,
    onBlur,
    testID = "file-upload",
    requestUrl,
    onSelect,
    onRemove,
    bucketDirectory,
    description,
    iconType = "description",
    iconColor = "background-accent-blue-strong",
    allowedFileTypes,
    downloadButtonInEditState = false,
    tryOpenFile,
    caseId,
    uploadType,
    useTrueFileName = false,
    orientation = "vertical",
    generateFileName = (file: File) => `${uuidv4()}-${file.name}`,
    onUpload,
  } = props;
  const { t } = useTranslation();

  const [uploadErrors, setErrors] = useState<string[]>([]);
  // use setParentFiles to avoid conflict with setFiles
  // use uploadProps to avoid passing setFiles to Upload, uploadProps excludes setFiles
  const { setFiles: setParentFiles, ...uploadProps } = props;
  const [uploading, setUploading] = useState<FileInfo[]>([]);
  const cancelFiles = useRef<FileInfo[]>([]);
  const currFiles = useRef<FileInfo[]>(files);

  useEffect(() => {
    currFiles.current = files;
  }, [files]);

  const requestUrlSubmitter = useSubmit(requestUrl);
  async function getPresignedUrl(file: FileInfo, overwrite?: boolean) {
    const body: {
      content_type: string | undefined;
      key: string | undefined;
      case?: string;
      type?: string;
      overwrite?: boolean;
    } = {
      content_type: file.type,
      key: file.key,
      overwrite,
    };

    if (caseId) {
      body.case = caseId;
      body.type = uploadType;
    }

    const response = await requestUrlSubmitter.submit(body);
    return response as DocumentCreateResponse;
  }

  const onFileUpload = async (fileInfo: FileInfo) => {
    try {
      const presignedUrlResponse = await getPresignedUrl(
        fileInfo,
        props.overwriteExistingFiles
      );
      const headers = new Headers();
      headers.append("Content-Type", String(fileInfo.type));

      // Only add x-amz-tagging header if tags exist and are not undefined
      if (
        typeof presignedUrlResponse.tags === "string" &&
        presignedUrlResponse.tags.trim() !== ""
      ) {
        headers.append("x-amz-tagging", presignedUrlResponse.tags);
      }
      const uploadResponse = await fetch(presignedUrlResponse.url, {
        method: "PUT",
        body: fileInfo.file,
        headers,
      });
      // fetch only rejects on network errors. A non-2xx S3 response (403/413/500)
      // resolves successfully, which previously caused us to record the file as
      // uploaded even though the object was never stored. Surface non-ok
      // responses as errors so onSelected's catch block runs and the user sees
      // the upload failed.
      if (!uploadResponse.ok) {
        throw new Error(
          `Upload to storage failed with status ${uploadResponse.status}`
        );
      }
    } finally {
      setUploading((currUploading) =>
        currUploading.filter((currFile) => currFile.key !== fileInfo.key)
      );
      currFiles.current = [...currFiles.current];

      onUpload?.("success");
    }
  };

  async function onSelected(fileInfo: FileInfo) {
    onUpload?.("init");

    try {
      onBlur && onBlur();
      await onFileUpload(fileInfo);
    } catch (e) {
      console.error("Error uploading file", e);
      setErrors((currErrors) => [
        ...currErrors,
        t("{{filename}} because something went wrong.", {
          filename: fileInfo.name,
        }),
      ]);

      onUpload?.("failure");

      return;
    }
    setFiles && setFiles([...currFiles.current, fileInfo]);
  }

  const handleFilesSelected = async (items: (File | FileDropItem)[]) => {
    setErrors([]);

    for (const item of items) {
      const file = "getFile" in item ? await item.getFile() : item;
      if (file && !allowedFileTypes?.includes(file.type)) {
        setErrors((currErrors) => [
          ...currErrors,
          t("{{filename}} because this file type is not supported.", {
            filename: file.name,
          }),
        ]);
        continue;
      }
      if (!bucketDirectory) {
        console.error(
          "FileUpload: bucketDirectory is undefined. Refusing to upload — " +
            "the ui_schema for this control must supply options.bucketDirectory."
        );
        setErrors((currErrors) => [
          ...currErrors,
          t("{{filename}} because something went wrong.", {
            filename: file.name,
          }),
        ]);
        continue;
      }
      const uniqueFileName = generateFileName(file);
      const fileName = useTrueFileName ? file.name : uniqueFileName;
      const fileInfo = fileToFileInfo(file, `${bucketDirectory}/${fileName}`);
      setUploading((currUploading) => [...currUploading, fileInfo]);

      try {
        if (!fileInfo.type) {
          throw new UploadError("unsupported");
        }

        if (props.fileTypes && !props.fileTypes.includes(fileInfo.type)) {
          throw new UploadError("unsupported");
        }

        if (
          fileInfo.size &&
          props.maxSizeBytes &&
          fileInfo.size > props.maxSizeBytes
        ) {
          throw new UploadError("too-big");
        }
        if (cancelFiles.current.includes(fileInfo)) {
          cancelFiles.current = cancelFiles.current.filter(
            (currFile) => currFile.key !== fileInfo.key
          );
          // FIRE-134: same reasoning as the catch block below — we're
          // skipping the onSelected hand-off, so clear the file from
          // `uploading` ourselves. Otherwise the spinner persists
          // indefinitely and errors on other files (which are gated on
          // `uploading.length < 1`) never render.
          setUploading((currUploading) =>
            currUploading.filter((currFile) => currFile.key !== fileInfo.key)
          );
          continue;
        }
      } catch (e) {
        // FIRE-134: pre-check failed, so we will NOT hand off to onSelected
        // (which would have cleared this file from `uploading` via its own
        // finally). Remove it from the uploading state here so the error
        // message renders (it's gated on `uploading.length < 1`) and the
        // spinner goes away.
        setUploading((currUploading) =>
          currUploading.filter((currFile) => currFile.key !== fileInfo.key)
        );

        if (e instanceof UploadError && e.code === "unsupported") {
          setErrors((currErrors) => [
            ...currErrors,
            t("{{filename}} because this file type is not supported.", {
              filename: fileInfo.name,
            }),
          ]);
          continue;
        }

        // FIRE-134: previously compared `e.message === "too-big"`, which is
        // always false because `new UploadError("too-big")` sets .code, not
        // .message (message is "Upload Error (too-big)"). That silently made
        // every `maxSizeBytes` consumer (AFP consent 10 MB, AuraData consent
        // 5 MB) show a generic error while still proceeding with the upload.
        if (e instanceof UploadError && e.code === "too-big") {
          setErrors((currErrors) => [
            ...currErrors,
            t("{{filename}} because the file size exceeds {{filesize}}.", {
              filename: fileInfo.name,
              filesize: formatFileSize(props.maxSizeBytes || 0),
            }),
          ]);
          continue;
        }

        setErrors((currErrors) => [
          ...currErrors,
          t("{{filename}} because something went wrong.", {
            filename: fileInfo.name,
          }),
        ]);
        continue;
      }

      // FIRE-134: pre-checks passed — only now do we actually hand the file
      // off for upload. This used to live in a `finally` block which ran
      // even after a pre-check `continue`, triggering a wasted network
      // round-trip (and in the case of size-rejects, still uploading the
      // file despite the error being shown to the user).
      if (onSelect) {
        // override for storybook
        await onSelect(file);
      } else {
        await onSelected(fileInfo);
      }
    }
  };

  const handleOnRemove = (fileToRemove: FileInfo) => {
    const updatedFiles = currFiles.current.filter(
      (file) => file.key !== fileToRemove.key
    );
    setFiles && setFiles(updatedFiles);
    onRemove?.(fileToRemove);
    currFiles.current = updatedFiles;
  };

  const showFileInput =
    props.multiple || (files.length < 1 && uploading.length < 1);

  const showFiles = files.length > 0 || uploading.length > 0;

  return (
    <Stack gap="space-1" testID={testID}>
      {label && (
        <FormLabel
          testID={`${testID}-label`}
          isRequired={props.isRequired}
          info={props.info}
        >
          {label}
        </FormLabel>
      )}
      {description && (
        <FormText testID={`${testID}-description`}>{description}</FormText>
      )}
      <Stack gap="space-3" orientation={orientation}>
        {showFileInput && (
          <Stack
            gap="space-1"
            className={css({
              width: "100%",
            })}
          >
            {!uploadProps.triggerButton && (
              <Upload
                testID={`${testID}-upload`}
                {...uploadProps}
                onFileSelected={async (items) => {
                  await handleFilesSelected(items);
                }}
              />
            )}

            <FieldErrors>{props.errors}</FieldErrors>

            {uploadErrors.length === 1 && (
              <FormFeedbackRAC type="invalid">
                {t("Unable to upload {{error}}", { error: uploadErrors[0] })}
              </FormFeedbackRAC>
            )}
            {uploadErrors.length > 1 && (
              <FormFeedbackRAC type="invalid">
                {t("The following files failed to upload:")}
                <List type="unordered">
                  {uploadErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </List>
              </FormFeedbackRAC>
            )}
          </Stack>
        )}
        {showFiles && (
          <Stack
            gap="space-2"
            data-testid={`${testID}-file-list`}
            className={css({ width: "100%" })}
          >
            {files.map((file, index) => {
              const [start, end] = truncatedFileName(file.name);
              return (
                <ActionList key={file.key}>
                  <ActionListItem
                    leading={
                      !downloadButtonInEditState &&
                      (file.type?.includes("image") && file.url ? (
                        <img src={file.url || file.content} alt={file.name} />
                      ) : (
                        <Icon
                          name={iconType}
                          color={iconColor}
                          size="space-6"
                          type="standard"
                        />
                      ))
                    }
                    trailing={
                      <Stack orientation="horizontal" gap="space-1">
                        {!uploadProps.isDisabled && (
                          <Link
                            textColor="text-weak"
                            testID="remove-file-link"
                            textStyle="global/body-secondary"
                            onPress={() => handleOnRemove(file)}
                          >
                            {t("Remove")}
                          </Link>
                        )}
                        {downloadButtonInEditState && tryOpenFile && (
                          <GhostIconButton
                            icon="download"
                            size="small"
                            onPress={() => tryOpenFile(file.key)}
                          />
                        )}
                      </Stack>
                    }
                    title={
                      <div className={styles.title}>
                        <Text
                          style="global/heading-medium"
                          truncate
                          className={styles.fileStart}
                        >
                          {start}
                        </Text>
                        <Text
                          style="global/heading-medium"
                          className={styles.fileEnd}
                        >
                          {end}
                        </Text>
                      </div>
                    }
                  >
                    <FileSubtitleRAC fileInfo={showFileInfo} file={file} />
                  </ActionListItem>
                </ActionList>
              );
            })}
            {uploading.map((file) => {
              return (
                <ActionList key={file.key}>
                  <ActionListItem
                    leading={<Loader type="spinner" size="small" />}
                    trailing={
                      <Link
                        textColor="text-weak"
                        testID="cancel-file-upload-link"
                        textStyle="global/body-secondary"
                        onPress={() => {
                          cancelFiles.current.push(file);
                          setUploading((currFiles) =>
                            currFiles.filter(
                              (currFile) => currFile.key !== file.key
                            )
                          );
                        }}
                      >
                        {t("Cancel")}
                      </Link>
                    }
                    title={file.name}
                  >
                    <FileSubtitleRAC fileInfo={showFileInfo} file={file} />
                  </ActionListItem>
                </ActionList>
              );
            })}
          </Stack>
        )}
        {!showFiles && uploadProps.isDisabled && (
          <Text style="global/body-primary" color="text-weak">
            {props.emptyStateText ?? t("No document found")}
          </Text>
        )}
        {uploadProps.triggerButton &&
          !uploadProps.isDisabled &&
          showFileInput && (
            <Upload
              testID={`${testID}-upload`}
              {...uploadProps}
              onFileSelected={async (items) => {
                await handleFilesSelected(items);
              }}
            />
          )}
      </Stack>
    </Stack>
  );
}

const styles = {
  title: css({
    display: "flex",
    flexWrap: "nowrap",
  }),
  fileStart: css({
    flexShrink: 1,
  }),
  fileEnd: css({
    flexShrink: 0,
  }),
};
